import * as z from "zod";
import { consentFormSchema as apiConsentFormSchema } from "@api/schemas/consent";
import type { paths } from "./api-types";
import type {
  CheckoutParticipant,
  ParticipantConsentPatchBody,
} from "./checkout-participant";

/**
 * UI version of the consent form schema.
 * Extends the API's base schema with UI-specific helper fields and overrides.
 */
export const consentFormSchema = apiConsentFormSchema.extend({
  // UI-specific additions and overrides
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  
  // The radio buttons return strings, so we allow both for form handling
  photoConsent: z.union([z.string(), z.boolean()]).nullable().optional(),
  
  // UI Helpers for conditional visibility and validation logic
  isUnder18: z.boolean().optional(),
  hasMedication: z.boolean().optional(),
  hasInjuries: z.boolean().optional(),
  hasDietaryRequirements: z.boolean().optional(),
  hasDisability: z.boolean().optional(),
});

export type ConsentFormValues = z.infer<typeof consentFormSchema>;

// Extract API body types from paths
export type PatchMeBody = NonNullable<
  paths["/api/user/me"]["patch"]["requestBody"]
>["content"]["application/json"];
export type CreateUserBody = NonNullable<
  paths["/api/user"]["post"]["requestBody"]
>["content"]["application/json"];
export type PatchUserBody = NonNullable<
  paths["/api/user/{id}"]["patch"]["requestBody"]
>["content"]["application/json"];

// Base type that all user API body types extend
type BaseUserApiBody = PatchMeBody;

/**
 * Strict version of the schema used for checkout validation.
 * This is UI-owned: it combines the base API shape with UI-specific helper toggles.
 */
export const strictConsentFormSchema = consentFormSchema.superRefine((data, ctx) => {
  const checkRequiredString = (field: keyof ConsentFormValues, label: string) => {
    const value = data[field];
    if (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} is required`,
        path: [field],
      });
    }
  };

  const checkRequiredTrue = (field: keyof ConsentFormValues, label: string) => {
    const value = data[field];
    if (value !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} must be accepted`,
        path: [field],
      });
    }
  };

  // Core required fields for a completed consent form
  const requiredStrings: Array<{ field: keyof ConsentFormValues; label: string }> =
    [
      { field: "consentFormParticipantPronouns", label: "Participant pronouns" },
      { field: "consentFormParticipantAddress", label: "Participant address" },
      { field: "consentFormEmergencyContact1Name", label: "Emergency contact 1 name" },
      {
        field: "consentFormEmergencyContact1Relationship",
        label: "Emergency contact 1 relationship",
      },
      { field: "consentFormEmergencyContact1Phone", label: "Emergency contact 1 phone" },
      { field: "consentFormEmergencyContact2Name", label: "Emergency contact 2 name" },
      {
        field: "consentFormEmergencyContact2Relationship",
        label: "Emergency contact 2 relationship",
      },
      { field: "consentFormEmergencyContact2Phone", label: "Emergency contact 2 phone" },
    ];

  const requiredTrues: Array<{ field: keyof ConsentFormValues; label: string }> = [
    { field: "consentFormCodeOfConductAccepted", label: "Code of conduct" },
    { field: "dataProcessingConsent", label: "Data processing consent" },
    { field: "emergencyTreatmentConsent", label: "Emergency treatment consent" },
  ];

  for (const req of requiredStrings) checkRequiredString(req.field, req.label);
  for (const req of requiredTrues) checkRequiredTrue(req.field, req.label);

  // Under-18 adds parent/carer requirements
  if (data.isUnder18) {
    const under18RequiredStrings: Array<{
      field: keyof ConsentFormValues;
      label: string;
    }> = [
      { field: "consentFormParticipantDateOfBirth", label: "Participant date of birth" },
      { field: "consentFormParentCarerFirstName", label: "Parent/carer first name" },
      { field: "consentFormParentCarerSurname", label: "Parent/carer surname" },
      { field: "consentFormParentCarerAddress", label: "Parent/carer address" },
      { field: "consentFormParentCarerRelationship", label: "Parent/carer relationship" },
      { field: "consentFormParentCarerContactNumber", label: "Parent/carer contact number" },
    ];

    for (const req of under18RequiredStrings) {
      checkRequiredString(req.field, req.label);
    }
  }

  // UI-specific medical requirements (based on toggles)
  if (data.hasMedication) checkRequiredString("consentFormMedicationDetails", "Medication details");
  if (data.hasInjuries) checkRequiredString("consentFormInjuriesDetails", "Injury details");
  if (data.hasDietaryRequirements) checkRequiredString("consentFormDietaryRequirementsDetails", "Dietary requirement details");
  if (data.hasDisability) checkRequiredString("consentFormDisabilityDetails", "Disability details");
});

/**
 * Strips UI-only helper fields and converts string booleans back to booleans for the API.
 * The API now uses form field names directly, so no mapping is needed.
 * Returns a type that is compatible with all user API body types.
 */
export function prepareConsentDataForApi(
  data: ConsentFormValues,
): BaseUserApiBody {
  const {
    isUnder18: _iu,
    hasMedication: _hm,
    hasInjuries: _hi,
    hasDietaryRequirements: _hdr,
    hasDisability: _hd,
    ...rest
  } = data;

  return {
    ...rest,
    email: rest.email || null, // Ensure email is null if empty string
    photoConsent:
      data.photoConsent === "true" || data.photoConsent === true
        ? true
        : data.photoConsent === "false" || data.photoConsent === false
          ? false
          : null,
    // Ensure marketingConsent is boolean or undefined (not null)
    marketingConsent: rest.marketingConsent ?? undefined,
  };
}

const UI_ONLY_CONSENT_FIELDS = new Set([
  "email",
  "isUnder18",
  "hasMedication",
  "hasInjuries",
  "hasDietaryRequirements",
  "hasDisability",
]);

function normalizePhotoConsentForApi(
  value: ConsentFormValues["photoConsent"],
): boolean | null | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (value === true || value === "true") {
    return true;
  }
  if (value === false || value === "false") {
    return false;
  }
  return undefined;
}

/**
 * Builds a participant consent PATCH body from form values.
 * Omits UI-only helper fields; server sets consent timestamps.
 */
export function prepareParticipantConsentPatch(
  participant: Pick<
    CheckoutParticipant,
    "userId" | "firstName" | "lastName"
  >,
  formData: Partial<ConsentFormValues>,
  eventRequirements: {
    requiresConsentForm: boolean;
    requiresPhotoConsent: boolean;
  },
): ParticipantConsentPatchBody {
  const identity: ParticipantConsentPatchBody = participant.userId
    ? { userId: participant.userId }
    : {
        firstName: participant.firstName,
        lastName: participant.lastName,
      };

  const patch: ParticipantConsentPatchBody = { ...identity };

  if (formData.dataProcessingConsent !== undefined) {
    patch.dataProcessingConsent = formData.dataProcessingConsent;
  }
  if (formData.consentFormCodeOfConductAccepted !== undefined) {
    patch.consentFormCodeOfConductAccepted =
      formData.consentFormCodeOfConductAccepted;
  }
  if (formData.marketingConsent !== undefined) {
    patch.marketingConsent = formData.marketingConsent;
  }

  if (eventRequirements.requiresConsentForm) {
    if (formData.emergencyTreatmentConsent !== undefined) {
      patch.emergencyTreatmentConsent = formData.emergencyTreatmentConsent;
    }

    const consentFormFields = [
      "consentFormParticipantDateOfBirth",
      "consentFormParticipantPronouns",
      "consentFormParticipantAddress",
      "consentFormParentCarerFirstName",
      "consentFormParentCarerSurname",
      "consentFormParentCarerAddress",
      "consentFormParentCarerRelationship",
      "consentFormParentCarerContactNumber",
      "consentFormEmergencyContact1Name",
      "consentFormEmergencyContact1Relationship",
      "consentFormEmergencyContact1Phone",
      "consentFormEmergencyContact2Name",
      "consentFormEmergencyContact2Relationship",
      "consentFormEmergencyContact2Phone",
      "consentFormImportantMedicalInformation",
      "consentFormMedicationDetails",
      "consentFormInjuriesDetails",
      "consentFormDietaryRequirementsDetails",
      "consentFormDisabilityDetails",
    ] as const;

    for (const field of consentFormFields) {
      if (formData[field] !== undefined) {
        if (field === "consentFormParticipantDateOfBirth") {
          patch.consentFormParticipantDateOfBirth = formData.isUnder18
            ? formData.consentFormParticipantDateOfBirth || null
            : null;
          continue;
        }

        if (
          field.startsWith("consentFormParentCarer") &&
          !formData.isUnder18
        ) {
          patch[field] = null;
          continue;
        }

        if (field === "consentFormMedicationDetails") {
          patch.consentFormMedicationDetails =
            formData.hasMedication && formData.consentFormMedicationDetails?.trim()
              ? formData.consentFormMedicationDetails.trim()
              : null;
          continue;
        }

        if (field === "consentFormInjuriesDetails") {
          patch.consentFormInjuriesDetails =
            formData.hasInjuries && formData.consentFormInjuriesDetails?.trim()
              ? formData.consentFormInjuriesDetails.trim()
              : null;
          continue;
        }

        if (field === "consentFormDietaryRequirementsDetails") {
          patch.consentFormDietaryRequirementsDetails =
            formData.hasDietaryRequirements &&
            formData.consentFormDietaryRequirementsDetails?.trim()
              ? formData.consentFormDietaryRequirementsDetails.trim()
              : null;
          continue;
        }

        if (field === "consentFormDisabilityDetails") {
          patch.consentFormDisabilityDetails =
            formData.hasDisability && formData.consentFormDisabilityDetails?.trim()
              ? formData.consentFormDisabilityDetails.trim()
              : null;
          continue;
        }

        const value = formData[field];
        patch[field] =
          typeof value === "string" ? value.trim() || null : value ?? null;
      }
    }
  }

  if (eventRequirements.requiresPhotoConsent && formData.photoConsent !== undefined) {
    const photoConsent = normalizePhotoConsentForApi(formData.photoConsent);
    if (photoConsent !== undefined) {
      patch.photoConsent = photoConsent;
    }
  }

  for (const field of UI_ONLY_CONSENT_FIELDS) {
    delete (patch as Record<string, unknown>)[field];
  }

  return patch;
}

export function mapCheckoutConsentToFormValues(
  consent: Record<string, unknown>,
): Partial<ConsentFormValues> {
  const photoConsent = consent.photoConsent;
  let photoConsentValue: ConsentFormValues["photoConsent"];
  if (photoConsent === null || photoConsent === undefined) {
    photoConsentValue = null;
  } else if (typeof photoConsent === "boolean") {
    photoConsentValue = photoConsent ? "true" : "false";
  } else {
    photoConsentValue = String(photoConsent);
  }

  return {
    firstName: typeof consent.firstName === "string" ? consent.firstName : "",
    lastName: typeof consent.lastName === "string" ? consent.lastName : "",
    consentFormParticipantDateOfBirth:
      typeof consent.consentFormParticipantDateOfBirth === "string"
        ? consent.consentFormParticipantDateOfBirth
        : "",
    consentFormParticipantPronouns:
      typeof consent.consentFormParticipantPronouns === "string"
        ? consent.consentFormParticipantPronouns
        : "",
    consentFormParticipantAddress:
      typeof consent.consentFormParticipantAddress === "string"
        ? consent.consentFormParticipantAddress
        : "",
    isUnder18: !!consent.consentFormParticipantDateOfBirth,
    consentFormParentCarerFirstName:
      typeof consent.consentFormParentCarerFirstName === "string"
        ? consent.consentFormParentCarerFirstName
        : "",
    consentFormParentCarerSurname:
      typeof consent.consentFormParentCarerSurname === "string"
        ? consent.consentFormParentCarerSurname
        : "",
    consentFormParentCarerAddress:
      typeof consent.consentFormParentCarerAddress === "string"
        ? consent.consentFormParentCarerAddress
        : "",
    consentFormParentCarerRelationship:
      typeof consent.consentFormParentCarerRelationship === "string"
        ? consent.consentFormParentCarerRelationship
        : "",
    consentFormParentCarerContactNumber:
      typeof consent.consentFormParentCarerContactNumber === "string"
        ? consent.consentFormParentCarerContactNumber
        : "",
    consentFormEmergencyContact1Name:
      typeof consent.consentFormEmergencyContact1Name === "string"
        ? consent.consentFormEmergencyContact1Name
        : "",
    consentFormEmergencyContact1Phone:
      typeof consent.consentFormEmergencyContact1Phone === "string"
        ? consent.consentFormEmergencyContact1Phone
        : "",
    consentFormEmergencyContact1Relationship:
      typeof consent.consentFormEmergencyContact1Relationship === "string"
        ? consent.consentFormEmergencyContact1Relationship
        : "",
    consentFormEmergencyContact2Name:
      typeof consent.consentFormEmergencyContact2Name === "string"
        ? consent.consentFormEmergencyContact2Name
        : "",
    consentFormEmergencyContact2Phone:
      typeof consent.consentFormEmergencyContact2Phone === "string"
        ? consent.consentFormEmergencyContact2Phone
        : "",
    consentFormEmergencyContact2Relationship:
      typeof consent.consentFormEmergencyContact2Relationship === "string"
        ? consent.consentFormEmergencyContact2Relationship
        : "",
    consentFormImportantMedicalInformation:
      typeof consent.consentFormImportantMedicalInformation === "string"
        ? consent.consentFormImportantMedicalInformation
        : "",
    consentFormMedicationDetails:
      typeof consent.consentFormMedicationDetails === "string"
        ? consent.consentFormMedicationDetails
        : "",
    consentFormInjuriesDetails:
      typeof consent.consentFormInjuriesDetails === "string"
        ? consent.consentFormInjuriesDetails
        : "",
    consentFormDietaryRequirementsDetails:
      typeof consent.consentFormDietaryRequirementsDetails === "string"
        ? consent.consentFormDietaryRequirementsDetails
        : "",
    consentFormDisabilityDetails:
      typeof consent.consentFormDisabilityDetails === "string"
        ? consent.consentFormDisabilityDetails
        : "",
    hasMedication: consent.hasMedication === true,
    hasInjuries: consent.hasInjuries === true,
    hasDietaryRequirements: consent.hasDietaryRequirements === true,
    hasDisability: consent.hasDisability === true,
    dataProcessingConsent: consent.dataProcessingConsent === true,
    emergencyTreatmentConsent: consent.emergencyTreatmentConsent === true,
    consentFormCodeOfConductAccepted:
      consent.consentFormCodeOfConductAccepted === true,
    marketingConsent: consent.marketingConsent === true,
    photoConsent: photoConsentValue,
  };
}

/**
 * Maps API user data to form values.
 * The API user type already uses form field names, so this is mainly for boolean conversions.
 */
export function mapApiUserToFormValues(
  user: {
    consentFormParticipantDateOfBirth?: string | null;
    consentFormParticipantPronouns?: string | null;
    consentFormParticipantAddress?: string | null;
    consentFormCodeOfConductAccepted?: boolean | null;
    marketingConsent?: boolean;
    photoConsent?: boolean | null;
    [key: string]: unknown;
  },
): Partial<ConsentFormValues> {
  return {
    consentFormParticipantDateOfBirth: user.consentFormParticipantDateOfBirth || "",
    consentFormParticipantPronouns: user.consentFormParticipantPronouns || "",
    consentFormParticipantAddress: user.consentFormParticipantAddress || "",
    consentFormCodeOfConductAccepted: user.consentFormCodeOfConductAccepted ?? false,
    marketingConsent: user.marketingConsent ?? false,
    photoConsent:
      user.photoConsent === null
        ? undefined
        : user.photoConsent?.toString(),
  };
}
